/* eslint-disable no-unused-vars */
import { useTheme } from '@material-ui/core';
import { Link as RouterLink, useLocation, useIntl } from 'umi';
import { Typography, Link } from '@material-ui/core';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import { BreadcrumbsBarStyled } from './BreadcrumbsBarStyled';
import { vipDataMap, NEW_MENU_CONFIG } from '@/constants';
import webStorage from '@/utils/storageManager';

const ACTIVE_CRUMB_STORAGE_KEY = 'SMG_ACTIVE_CRUMB';

// 读取"最近一次被点击的叶子菜单"对应的完整面包屑层级：
//   { href, items: [menuItem, menuItem, ...] }
// 每个 menuItem 是完整的菜单配置对象（含 title/href/auth/isChild 等）。
function readActiveCrumb(pathname) {
  try {
    const raw = sessionStorage.getItem(ACTIVE_CRUMB_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const crumb = JSON.parse(raw);
    if (crumb && crumb.href === pathname && Array.isArray(crumb.items)) {
      return crumb;
    }
    return null;
  } catch (e) {
    return null;
  }
}

// 在 NEW_MENU_CONFIG 中精确查找 href 完全等于 pathname 的节点，
// 返回从根到该节点的祖先菜单项数组（含自身）。
//   - 不做路径前缀匹配（避免 /platform 误命中 /platformxxx）；
//   - 命中后会把这个链路写回 sessionStorage，下一次访问直接命中缓存；
//   - 找不到时返回 null（面包屑不渲染任何内容）。
function findAndCacheCrumb(pathname) {
  let found = null;

  function walk(items, ancestors) {
    for (const item of items) {
      if (item.href === pathname) {
        found = [...ancestors, item];
        return true; // 找到后立即停止递归
      }
      if (item.items && walk(item.items, [...ancestors, item])) {
        return true;
      }
    }
    return false;
  }

  walk(NEW_MENU_CONFIG, []);

  if (found) {
    try {
      sessionStorage.setItem(
        ACTIVE_CRUMB_STORAGE_KEY,
        JSON.stringify({ href: pathname, items: found }),
      );
    } catch (e) {
      // sessionStorage 不可用时静默失败
    }
  }

  return found;
}

// 处理菜单项的 title：支持 tenantId 替换"直营市场/大连会员数据"。
function resolveTitle(item) {
  const tenantId = webStorage.get('TENANT_ID');
  let title = item.title;
  if (tenantId !== '10000') {
    if (title === '直营市场会员数据') {
      title = '经销商会员数据';
    } else if (title === '大连会员数据') {
      title = `${vipDataMap.get(tenantId) || ''}会员数据`;
    }
  }
  return title;
}

const BreadcrumbsBar = () => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const theme = useTheme();
  const location = useLocation();
  const { pathname } = location;

  // 1) 优先读 SMG_ACTIVE_CRUMB（菜单点击或上一次面包屑渲染时写入）。
  let items = readActiveCrumb(pathname)?.items;
  // 2) 找不到时（例如通过业务按钮进入详情页 / 刷新 / 直接输入 URL），
  //    按 NEW_MENU_CONFIG 精确匹配并写入。
  //    这是核心兜底——不依赖 NavItem 的 onClick 触发，
  //    即使 onClick 被 Material-UI / RouterLink 吞掉，面包屑仍能正确渲染。
  if (!items) {
    items = findAndCacheCrumb(pathname);
  }
  // 3) 还找不到就不渲染（避免按 pathSnippets 一段段乱猜）。
  if (!items || items.length === 0) {
    return (
      <BreadcrumbsBarStyled theme={theme} aria-label="breadcrumb">
        <Breadcrumbs />
      </BreadcrumbsBarStyled>
    );
  }

  const userAuths = webStorage.get('USER')?.authorities;

  const breadcrumbItems = items.map((item, index) => {
    const isLast = index === items.length - 1;
    const hasAuth = !item.auth || userAuths?.includes(item.auth);
    const title = resolveTitle(item);
    // 父级（可点击）：跳转到该层级对应的 href（保持父级位置）；
    // 最后一级（不可点击）：始终是当前 pathname，显示标题。
    const linkTo = isLast ? pathname : item.href;
    return hasAuth ? (
      <Link
        key={`${item.href}-${index}`}
        variant="body1"
        color="inherit"
        to={linkTo}
        component={RouterLink}
      >
        {title}
      </Link>
    ) : (
      <Typography key={`${item.href}-${index}`} color="textSecondary">
        {title}
      </Typography>
    );
  });

  return (
    <BreadcrumbsBarStyled theme={theme} aria-label="breadcrumb">
      <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>
    </BreadcrumbsBarStyled>
  );
};

export default BreadcrumbsBar;
